<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useForm, useToast } from 'vuestic-ui'
import { useI18n } from 'vue-i18n'
import { getById, updateById } from '../../../core/requests/Contacts'
import { getAllCompanies } from '../../../core/requests/Company'
import { validators } from '../../../services/utils'
import { onlyNumbersNoDigits } from '../../../core/helpers'

const vOnlyNumbers = onlyNumbersNoDigits

const emit = defineEmits(['close'])

const props = defineProps({
  id: {
    type: String,
    default: '',
  },
})

const { t } = useI18n()
const isLoading = ref(false)
const { isValid, validate, reset } = useForm('formRef')
const form: any = ref({
  havecompany: false,
})
const companies: any = ref([])
const { notify } = useToast()
const doProcess = async () => {
  if (validate()) {
    isLoading.value = true
    const {
      fullname,
      document_type,
      document,
      ruc,
      email,
      email_corporative,
      cellphone,
      whatsapp,
      address,
      havecompany,
      company,
    } = form.value

    const documentType = document_type?.code

    await updateById(props.id, {
      fullname,
      document_type: documentType,
      document,
      ruc,
      email,
      email_corporative,
      cellphone,
      whatsapp,
      address,
      havecompany,
      company,
    })

    notify({
      message: 'Cliente actualizado correctamente',
      color: 'success',
    })

    reset()
    isLoading.value = false
    emit('close')
  }
}

const selectedCompany: any = computed(() => companies.value.find((f: any) => f.id === form.value.company))

onMounted(async () => {
  isLoading.value = true
  watch(
    () => form.value.havecompany,
    async (val) => {
      if (val) {
        isLoading.value = true
        await getCompanies()
        isLoading.value = false
      }
    },
  )

  const contact: any = await getById(props.id)
  form.value = contact
  form.value.havecompany = !!contact.havecompany
  form.value.document_type = { code: contact.document_type, text: 'DNI' }
  isLoading.value = false
})

const documentTypes = [
  { code: 1, text: 'DNI' },
  { code: 2, text: 'C.E' },
  { code: 3, text: 'PASAPORTE' },
]
// const countries = [{ code: 1, text: 'PERU' }]
const openSunatRuc = () => {
  window.open(
    'https://e-consultaruc.sunat.gob.pe/cl-ti-itmrconsruc/FrameCriterioBusquedaWeb.jsp',
    '_blank',
    'menubar=1,resizable=0,width=600,height=500',
  )
}
const getCompanies = async () => {
  const cData = await getAllCompanies()
  companies.value = cData
}
</script>

<template>
  <VaInnerLoading :loading="isLoading" style="--va-inner-loading-min-width: 100%">
    <div class="bg-blue-50">
      <div>
        <div class="flex justify-between align-center justify-center">
          <h1 class="h5 font-bold m-0">{{ t('contacts.contactDetail') }}</h1>
          <div class="flex gap-2">
            <VaButton icon="save" color="primary" :disabled="!isValid" :loading="isLoading" @click="doProcess">
              <span class="text-xs px-1">{{ t('contacts.updateContact') }}</span>
            </VaButton>
          </div>
        </div>
      </div>

      <div class="py-4 new-customer gap-8">
        <VaForm ref="formRef" immediate class="gap-2">
          <div class="w-full bg-white p-2">
            <!-- <p class="text-sm font-semibold mb-3">Datos personales</p> -->
            <div class="gap-2">
              <div class="w-full columns-1 sm:columns-4 gap-4">
                <div>
                  <VaInput
                    v-model="form.fullname"
                    stateful
                    name="fullname"
                    placeholder="Sin Información"
                    clearable
                    maxlength="50"
                    label="Nombres completos"
                    :loading="isLoading"
                    :rules="[validators.required]"
                  />
                </div>
                <div>
                  <VaSelect
                    v-model="form.document_type"
                    name="document_type"
                    placeholder="Sin Información"
                    :options="documentTypes"
                    no-options-text="-- No hay items --"
                    label="Tipo de documento"
                  />
                </div>
                <div>
                  <VaInput
                    v-model="form.document"
                    v-only-numbers
                    name="document"
                    placeholder="Sin Información"
                    stateful
                    clearable
                    label="N° Documento"
                    maxlength="22"
                  />
                </div>
                <div>
                  <VaInput
                    v-model="form.ruc"
                    v-only-numbers
                    name="ruc"
                    maxlength="20"
                    placeholder="Sin Información"
                    stateful
                    clearable
                    label="RUC"
                  >
                    <template #appendInner>
                      <VaIcon name="open_in_new" class="cursor-pointer" @click="openSunatRuc()" />
                    </template>
                  </VaInput>
                </div>
              </div>

              <div class="w-full mt-3">
                <div class="w-full">
                  <VaInput
                    v-model="form.address"
                    name="address"
                    placeholder="Sin Información"
                    stateful
                    clearable
                    label="Dirección de contacto"
                  />
                </div>
              </div>

              <div class="w-full columns-1 sm:columns-4 gap-4 mt-3 pb-3">
                <div>
                  <VaInput
                    v-model="form.email"
                    name="email"
                    placeholder="Sin Información"
                    stateful
                    maxlength="25"
                    clearable
                    label="Correo electrónico"
                  >
                    <template #prependInner>
                      <VaIcon name="alternate_email" class="material-symbols-outlined -mx-1.5" color="secondary" />
                    </template>
                  </VaInput>
                </div>
                <div>
                  <VaInput
                    v-model="form.email_corporative"
                    name="email_corporative"
                    placeholder="Sin Información"
                    stateful
                    clearable
                    maxlength="25"
                    label="Correo corporativo"
                  >
                    <template #prependInner>
                      <VaIcon name="alternate_email" class="material-symbols-outlined -mx-1.5" color="secondary" />
                    </template>
                  </VaInput>
                </div>

                <div>
                  <VaInput
                    v-model="form.cellphone"
                    v-only-numbers
                    placeholder="Sin Información"
                    maxlength="12"
                    name="cellphone"
                    stateful
                    clearable
                    label="Teléfono"
                    :rules="[validators.required]"
                  >
                    <template #prependInner>
                      <VaIcon name="call" class="material-symbols-outlined -mx-1.5" color="secondary" />
                    </template>
                  </VaInput>
                </div>
                <div>
                  <VaInput
                    v-model="form.whatsapp"
                    v-only-numbers
                    name="whatsapp"
                    maxlength="12"
                    placeholder="Sin Información"
                    stateful
                    clearable
                    label="WhatsApp"
                  >
                    <template #prependInner>
                      <VaIcon name="call" class="material-symbols-outlined -mx-1.5" color="secondary" />
                    </template>
                  </VaInput>
                </div>
              </div>

              <div class="w-full mt-5">
                <VaSwitch
                  v-model="form.havecompany"
                  class="text-xs"
                  name="havecompany"
                  label="Representa una empresa"
                  size="small"
                />
              </div>

              <div v-if="form.havecompany" class="columns-1 sm:columns-3 mt-5">
                <div>
                  <VaSelect
                    v-model="form.company"
                    name="company_id"
                    class="text-xs"
                    :options="companies"
                    track-by="id"
                    value-by="id"
                    text-by="socialreason"
                    searchable
                    no-options-text="-- No hay items --"
                    clearable
                    search-placeholder-text="Escribe el RUC"
                    :rules="[validators.required]"
                    label="Selecciona la empresa"
                  />
                </div>
              </div>

              <div v-if="form.havecompany && selectedCompany" class="bg-blue-50 px-3 py-2 mt-5">
                <p class="font-bold text-sm mb-3">{{ selectedCompany.socialreason }}</p>
                <div class="columns-3 sm:columns-4">
                  <div>
                    <VaInput
                      :model-value="selectedCompany.ruc"
                      background="#eee"
                      name="whatsapp"
                      readonly
                      class="opacity-85 cursor-not-allowed"
                      placeholder="Sin Información"
                      label="RUC"
                    />
                  </div>
                  <div>
                    <VaInput
                      :model-value="selectedCompany.phone"
                      background="#eee"
                      name="whatsapp"
                      placeholder="Sin Información"
                      readonly
                      class="opacity-85 cursor-not-allowed"
                      label="Teléfono"
                    />
                  </div>
                  <div>
                    <VaInput
                      :model-value="selectedCompany.whatsapp"
                      background="#eee"
                      name="whatsapp"
                      readonly
                      placeholder="Sin Información"
                      class="opacity-85 cursor-not-allowed"
                      label="WhatsApp"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </VaForm>
      </div>

      <div class="pb-3">
        <div class="flex justify-end">
          <div class="flex gap-2">
            <VaButton icon="save" color="primary" :disabled="!isValid" :loading="isLoading" @click="doProcess">
              <span class="text-xs px-1">{{ t('contacts.updateContact') }}</span>
            </VaButton>
          </div>
        </div>
      </div>
    </div>
  </VaInnerLoading>
</template>

<style lang="scss">
.new-customer {
  &-tabs {
    .va-tabs__content {
      width: 100%;
    }
  }

  .float-profile {
    position: absolute;
    top: 0;
    right: 0;
  }
}
</style>
